import {
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useColorMode,
  Box,
  Link,
} from "@chakra-ui/react";
import { QuestionIcon } from "@chakra-ui/icons";
import { motion, useAnimation } from "framer-motion";
import { useEffect } from "react";

const MotionIconButton = motion(IconButton);

export default function FloatingFAQ() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { colorMode } = useColorMode();
  const controls = useAnimation();

  const EDGE_Y = 80; // ✅ minimum distance top/bottom

  const getRandomStart = () => {
    const x = Math.random() * window.innerWidth; // full width
    const y = EDGE_Y + Math.random() * (window.innerHeight - EDGE_Y * 2);
    return { x, y };
  };

  useEffect(() => {
    const { x, y } = getRandomStart();
    controls.set({
      top: `${y}px`,
      left: `${x}px`,
    });

    const animateLoop = async () => {
      while (true) {
        await controls.start({
          top: `${
            EDGE_Y + Math.random() * (window.innerHeight - EDGE_Y * 2)
          }px`,
          left: `${Math.random() * window.innerWidth}px`,
          transition: { duration: 30, ease: "easeInOut" },
        });
      }
    };
    animateLoop();
  }, [controls]);

  return (
    <>
      <MotionIconButton
        icon={<QuestionIcon />}
        aria-label="How It Works"
        position="fixed"
        zIndex={10}
        size="lg"
        bg={colorMode === "light" ? "blackAlpha.400" : "whiteAlpha.200"}
        color={colorMode === "light" ? "black" : "white"}
        borderRadius="full"
        _hover={{
          bg: colorMode === "light" ? "blackAlpha.600" : "whiteAlpha.300",
        }}
        onClick={onOpen}
        animate={controls}
      />

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={{ base: "lg", md: "2xl", lg: "3xl" }}
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay bg="rgba(0, 0, 0, 0.4)" backdropFilter="blur(8px)" />
        <ModalContent
          bg={
            colorMode === "light"
              ? "rgba(255, 255, 255, 0.6)"
              : "rgba(255, 255, 255, 0.1)"
          }
          border="1px solid rgba(255, 255, 255, 0.2)"
          borderRadius="2xl"
          backdropFilter="blur(20px)"
          boxShadow={
            colorMode === "light"
              ? "0 4px 40px rgba(0, 0, 0, 0.1)"
              : "0 4px 40px rgba(0, 0, 0, 0.3)"
          }
          color={colorMode === "light" ? "gray.800" : "white"}
          mx={{ base: 4, md: 0 }}
        >
          <ModalHeader>How Our Lottery Works</ModalHeader>
          <ModalCloseButton />
          <ModalBody
            css={{
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Box
              as="section"
              fontSize="md"
              lineHeight="1.7"
              whiteSpace="pre-line"
            >
              {`🎯 HOW YOU CAN WIN
🥇 Tier 4: All 6 digits match exactly.
🥈 Tier 3: First 4 digits match, last 2 scrambled.
🥉 Tier 2: First 2 digits match, last 4 scrambled.
🔸 Tier 1: First 1 digit matches, last 5 scrambled.

🏅 Tier 0 (Special Numbers Bonus)
• 0d: Special — 000000 → 50,000 USDT 🎉
• 0c: 600 USDT – Numbers 123456 or 654321 (Shown → 6,000 USDT)
• 0b: 300 USDT – All identical digits (e.g., 111111, 777777) (Shown → 3,000 USDT)
• 0a: 150 USDT – Five identical digits (e.g., 11111x or x11111) (Shown → 1,500 USDT)
• Scaled by tickets bought: 1–2000 → ⅓, 2001–5000 → ⅔, 5001–10000 → full bonus

💡 Instant payout, separate from the prize pool.
“Shown” means the number appeared in the app’s daily draw list for the window.

💰 HOW THE PRIZE POOL IS SHARED
• Tier 4: 10 points + 50 USDT bonus
• Tier 3: 6 points
• Tier 2: 3 points
• Tier 1: 2 points
The daily amounts of Tier-0 rewards (6,000 USDT, 3,000 USDT, and 1,500 USDT) are added separately into the main pool.
Bonus payouts are separate and don’t reduce the prize pool amount.
95% of the prize pool will be distributed (5% fee retained).

⚖️ SCALING FOR FAIRNESS
If multiple winners exist, the prize pool is split proportionally by tier points.

📆 Payouts are distributed daily starting at 21:00 UTC and may take 12 hours to complete.
Payout amount depends on the prize pool and winning tiers.`}
            </Box>
            <Box mt={4} textAlign="center" fontSize="sm">
              <Link
                href="/terms"
                isExternal
                color={colorMode === "light" ? "blue.600" : "blue.300"}
                textDecoration="underline"
              >
                Please read Terms & Conditions before participation
              </Link>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
